import { CRASH_LIST_VIEW } from '../../pages/Statistics/statistics-dataset-context';
import {
  buildCrashListAllUrl,
  CRASH_LIST_EXPORT_PAGE_SIZE,
  parseCrashListTotalCount,
} from '../../utils/crash-list-pagination';
import { dedupeRowsByCrashId, filterExportRows } from './crash-list-nested-rows.helper';

const CRASH_LIST_EXPORT_FILENAME_BY_VIEW = Object.freeze({
  [CRASH_LIST_VIEW.PENDING]: 'to_validate_crashes.csv',
  [CRASH_LIST_VIEW.VALIDATED]: 'validated_crashes.csv',
  [CRASH_LIST_VIEW.SOURCE]: 'source_crashes.csv',
});

export function getCrashListExportFilename(view) {
  return CRASH_LIST_EXPORT_FILENAME_BY_VIEW[view] ?? 'crash-list.csv';
}

function escapeCsvCell(value) {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function buildCsvContent(columns, rows) {
  const exportColumns = columns.filter(
    (col) => col.field && !['validated_lock', 'actions'].includes(col.field),
  );
  const header = exportColumns.map((col) => escapeCsvCell(col.headerName ?? col.field)).join(',');
  const body = rows.map((row) =>
    exportColumns.map((col) => {
      const raw = row[col.field];
      return escapeCsvCell(raw);
    }).join(','),
  );
  return [header, ...body].join('\n');
}

function triggerCsvDownload(filename, csvContent) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

async function fetchAllCrashListCrashes(options) {
  const { baseUrl, token, request, urlOptions } = options;
  const collected = [];
  let offset = 0;
  let totalCount = null;

  while (true) {
    const pageUrl = buildCrashListAllUrl(baseUrl, urlOptions);
    pageUrl.searchParams.set('limit', String(CRASH_LIST_EXPORT_PAGE_SIZE));
    pageUrl.searchParams.set('offset', String(offset));

    const res = await request(
      pageUrl.toString(),
      'GET',
      null,
      { Authorization: `Bearer ${token}` },
    );
    if (res?.responseAnyError || !Array.isArray(res?.responseData?.crashes)) {
      throw new Error('Export failed');
    }

    if (totalCount == null) {
      totalCount = parseCrashListTotalCount(res.responseData);
    }

    const page = res.responseData.crashes;
    if (page.length === 0) break;

    collected.push(...page);
    offset += CRASH_LIST_EXPORT_PAGE_SIZE;

    if (totalCount != null && offset >= totalCount) break;
    if (page.length < CRASH_LIST_EXPORT_PAGE_SIZE) break;
  }

  return collected;
}

export async function exportCrashListCsv(options) {
  const {
    baseUrl,
    token,
    request,
    datasetParams,
    panelFilters,
    queryFilters,
    filterModel,
    configuration,
    sortModel,
    columns,
    view,
    filename = 'crash-list.csv',
  } = options;

  let exportRows = await fetchAllCrashListCrashes({
    baseUrl,
    token,
    request,
    urlOptions: {
      datasetParams,
      panelFilters,
      queryFilters,
      filterModel,
      configuration,
      sortModel,
    },
  });
  if (options.prepareRows) {
    exportRows = options.prepareRows(exportRows);
  }
  exportRows = dedupeRowsByCrashId(exportRows);
  exportRows = filterExportRows(exportRows, view);
  const csvContent = buildCsvContent(columns, exportRows);
  triggerCsvDownload(filename, csvContent);
}
